import { useParams } from "react-router";
import { SkeletonCard } from "@/components/skeleton";
import { useApi } from "@/hooks/use-api";
import { EmailPreview } from "./email-preview";
import { sanitizeCampaignHtml } from "./html-sanitizer";
import {
  AlertCircle,
  Brain,
  DollarSign,
  Eye,
  MailCheck,
  MousePointerClick,
  Sparkles,
  TrendingUp,
} from "lucide-react";

interface CampaignIntelligenceAvailability {
  available: boolean;
  reason: string;
}

interface CampaignIntelligence {
  campaignId: string;
  metrics: {
    totalRecipients: number;
    sentCount: number;
    deliveredCount: number;
    uniqueOpenCount: number;
    uniqueClickCount: number;
    bounceCount: number;
    complaintCount: number;
    unsubscribeCount: number;
    deliveryRatePct: number;
    openRatePct: number;
    clickRatePct: number;
    bounceRatePct: number;
    complaintRatePct: number;
    unsubscribeRatePct: number;
    clickToOpenRatePct: number;
  };
  intelligence: {
    prediction: {
      predictedOpenRatePct: number | null;
      predictedClickRatePct: number | null;
      confidence: "high" | "medium" | "low" | null;
      factors: string[];
      availability: CampaignIntelligenceAvailability;
    };
    revenue: {
      amount: number | null;
      availability: CampaignIntelligenceAvailability;
    };
    topLinks: {
      items: Array<{ url: string; clicks: number; revenue: number | null }>;
      availability: CampaignIntelligenceAvailability;
    };
    engagementBySection: {
      items: Array<{ section: string; opensPct: number; clicks: number }>;
      availability: CampaignIntelligenceAvailability;
    };
    linkHeatmap: {
      renderedHtml: string | null;
      items: Array<{ url: string; label: string; clicks: number; densityPct: number }>;
      availability: CampaignIntelligenceAvailability;
    };
    aiRecommendations: {
      items: string[];
      availability: CampaignIntelligenceAvailability;
    };
  };
}

function MetricCard({
  icon: Icon,
  label,
  value,
  hint,
}: {
  icon: typeof Eye;
  label: string;
  value: string;
  hint: string;
}) {
  return (
    <div className="stagger-item rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-4">
      <div className="mb-2 flex items-center gap-2 text-xs uppercase text-[var(--color-muted-foreground)]">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-2xl font-bold text-[var(--color-foreground)]">{value}</div>
      <div className="mt-1 text-xs text-[var(--color-muted-foreground)]">{hint}</div>
    </div>
  );
}

function UnavailablePanel({
  title,
  icon: Icon,
  reason,
}: {
  title: string;
  icon: typeof Brain;
  reason: string;
}) {
  return (
    <div className="rounded-xl border border-dashed border-[var(--color-border)] bg-[var(--color-surface)] p-5">
      <h2 className="mb-2 flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
        <Icon className="h-4 w-4" />
        {title}
      </h2>
      <div className="flex items-start gap-2 text-sm text-[var(--color-muted-foreground)]">
        <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--color-warning)]" />
        <span>{reason}</span>
      </div>
    </div>
  );
}

function decorateHtmlWithHeatmap(
  html: string,
  items: CampaignIntelligence["intelligence"]["linkHeatmap"]["items"],
): string {
  if (typeof DOMParser === "undefined") {
    return sanitizeCampaignHtml(html);
  }

  const parsed = new DOMParser().parseFromString(sanitizeCampaignHtml(html), "text/html");
  for (const item of items) {
    const anchors = Array.from(parsed.querySelectorAll("a")).filter((anchor) => anchor.getAttribute("href") === item.url);
    anchors.forEach((anchor) => {
      const intensity = Math.max(0.08, item.densityPct / 100);
      const currentStyle = anchor.getAttribute("style") ?? "";
      anchor.setAttribute(
        "style",
        `${currentStyle}; background: rgba(245, 158, 11, ${intensity}); box-shadow: inset 0 0 0 1px rgba(245, 158, 11, 0.35); border-radius: 6px; padding: 2px 4px;`,
      );
      anchor.setAttribute("title", `${item.clicks} clicks`);
      anchor.setAttribute("data-heatmap-density", String(item.densityPct));
    });
  }

  return parsed.body.innerHTML;
}

export default function CampaignIntelligencePage() {
  const { id } = useParams<{ id: string }>();
  const { data, isLoading, error } = useApi<CampaignIntelligence>(
    id ? `/api/email/campaigns/${id}/analytics` : "",
  );

  if (!id) {
    return (
      <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 text-sm text-[var(--color-muted-foreground)]">
        Campaign ID is missing.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-[var(--color-foreground)]">Campaign Intelligence</h1>
        <p className="text-sm text-[var(--color-muted-foreground)]">
          Campaign {id} - real delivery and engagement metrics with modeled pre-send guidance.
        </p>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-3 gap-4">
          {[1, 2, 3].map((item) => (
            <SkeletonCard key={item} className="h-24 rounded-xl" />
          ))}
        </div>
      ) : error || !data ? (
        <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-6 text-sm text-[var(--color-muted-foreground)]">
          {error ?? "Campaign intelligence is currently unavailable."}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
            <MetricCard
              icon={Eye}
              label="Open Rate"
              value={`${data.metrics.openRatePct}%`}
              hint={`${data.metrics.uniqueOpenCount} unique opens from ${data.metrics.deliveredCount} delivered`}
            />
            <MetricCard
              icon={MousePointerClick}
              label="Click Rate"
              value={`${data.metrics.clickRatePct}%`}
              hint={`${data.metrics.uniqueClickCount} unique clicks, ${data.metrics.clickToOpenRatePct}% click-to-open`}
            />
            <MetricCard
              icon={MailCheck}
              label="Delivery Rate"
              value={`${data.metrics.deliveryRatePct}%`}
              hint={`${data.metrics.sentCount} sent, ${data.metrics.bounceCount} bounced`}
            />
            <MetricCard
              icon={TrendingUp}
              label="Recipients"
              value={data.metrics.totalRecipients.toLocaleString()}
              hint={`${data.metrics.complaintCount} complaints, ${data.metrics.unsubscribeCount} unsubscribes`}
            />
          </div>

          {data.intelligence.prediction.availability.available ? (
            <div className="rounded-xl border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-5">
              <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-[var(--color-ai)]">
                <Sparkles className="h-4 w-4" />
                Modeled Performance
              </h2>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                <div className="rounded-lg bg-white/70 px-4 py-3">
                  <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Predicted Open Rate</div>
                  <div className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">
                    {data.intelligence.prediction.predictedOpenRatePct}%
                  </div>
                </div>
                <div className="rounded-lg bg-white/70 px-4 py-3">
                  <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Predicted Click Rate</div>
                  <div className="mt-1 text-2xl font-semibold text-[var(--color-foreground)]">
                    {data.intelligence.prediction.predictedClickRatePct}%
                  </div>
                </div>
                <div className="rounded-lg bg-white/70 px-4 py-3">
                  <div className="text-xs uppercase text-[var(--color-muted-foreground)]">Confidence</div>
                  <div className="mt-1 text-2xl font-semibold capitalize text-[var(--color-foreground)]">
                    {data.intelligence.prediction.confidence}
                  </div>
                </div>
              </div>
              {data.intelligence.prediction.factors.length > 0 ? (
                <ul className="mt-4 space-y-2">
                  {data.intelligence.prediction.factors.map((factor) => (
                    <li key={factor} className="stagger-item text-sm text-[var(--color-foreground)]">
                      {factor}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : (
            <UnavailablePanel
              title="Modeled Performance"
              icon={Sparkles}
              reason={data.intelligence.prediction.availability.reason}
            />
          )}

          {data.intelligence.revenue.availability.available ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <DollarSign className="h-4 w-4" />
                Revenue Attributed
              </h2>
              <div className="text-2xl font-bold text-[var(--color-foreground)]">
                ${data.intelligence.revenue.amount?.toLocaleString() ?? "0"}
              </div>
            </div>
          ) : (
            <UnavailablePanel
              title="Revenue Attribution"
              icon={DollarSign}
              reason={data.intelligence.revenue.availability.reason}
            />
          )}

          {data.intelligence.topLinks.availability.available ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="mb-4 text-sm font-semibold text-[var(--color-foreground)]">Top Links</h2>
              <div className="space-y-2">
                {data.intelligence.topLinks.items.map((link, idx) => (
                  <div key={link.url} className="stagger-item flex items-center gap-3 rounded-lg bg-[var(--color-surface)] px-4 py-3">
                    <span className="text-sm font-bold text-[var(--color-primary)]">#{idx + 1}</span>
                    <span className="flex-1 truncate font-mono text-sm text-[var(--color-foreground)]">{link.url}</span>
                    <span className="text-xs text-[var(--color-muted-foreground)]">{link.clicks} clicks</span>
                    <span className="text-sm font-medium text-[var(--color-success)]">
                      {link.revenue == null ? "N/A" : `$${link.revenue}`}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <UnavailablePanel
              title="Top Links"
              icon={MousePointerClick}
              reason={data.intelligence.topLinks.availability.reason}
            />
          )}

          {data.intelligence.linkHeatmap.availability.available && data.intelligence.linkHeatmap.renderedHtml ? (
            <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-background)] p-5">
              <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-[var(--color-foreground)]">
                <MousePointerClick className="h-4 w-4" />
                Link Heatmap
              </h2>
              <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_320px]">
                <EmailPreview
                  title="Campaign heatmap preview"
                  html={decorateHtmlWithHeatmap(
                    data.intelligence.linkHeatmap.renderedHtml,
                    data.intelligence.linkHeatmap.items,
                  )}
                />
                <div className="space-y-3">
                  {data.intelligence.linkHeatmap.items.map((item) => (
                    <div key={`${item.url}-${item.label}`} className="rounded-lg bg-[var(--color-surface)] px-4 py-3">
                      <div className="flex items-center justify-between gap-3">
                        <div className="min-w-0">
                          <div className="truncate text-sm font-medium text-[var(--color-foreground)]">{item.label}</div>
                          <div className="truncate font-mono text-xs text-[var(--color-muted-foreground)]">{item.url}</div>
                        </div>
                        <div className="text-right">
                          <div className="text-sm font-semibold text-[var(--color-foreground)]">{item.clicks}</div>
                          <div className="text-xs text-[var(--color-muted-foreground)]">{item.densityPct}% density</div>
                        </div>
                      </div>
                      <div className="mt-3 h-2 rounded-full bg-amber-100">
                        <div className="h-2 rounded-full bg-amber-500" style={{ width: `${item.densityPct}%` }} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <UnavailablePanel
              title="Link Heatmap"
              icon={MousePointerClick}
              reason={data.intelligence.linkHeatmap.availability.reason}
            />
          )}

          {data.intelligence.aiRecommendations.availability.available ? (
            <div className="rounded-xl border border-[var(--color-ai-border)] bg-[var(--color-ai-light)] p-5">
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-[var(--color-ai)]">
                <Brain className="h-4 w-4" />
                AI Recommendations
              </h2>
              <ul className="space-y-2">
                {data.intelligence.aiRecommendations.items.map((item) => (
                  <li key={item} className="stagger-item flex items-start gap-2 text-sm text-[var(--color-foreground)]">
                    <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-[var(--color-ai)]" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <UnavailablePanel
              title="AI Recommendations"
              icon={Brain}
              reason={data.intelligence.aiRecommendations.availability.reason}
            />
          )}
        </>
      )}
    </div>
  );
}
